// @ts-strict-ignore
import { AfterViewInit, Component, EventEmitter, HostBinding, OnDestroy, OnInit, Output, Signal, viewChild } from '@angular/core';
import { ProjectNavigationModel, ProjectNavigationResult, ProjectNavigationTab } from '../project-navigation.model';
import { ProjectNavigationComponent } from '../project-navigation.component';
import { CompositeArticleConfigurationVO, CompositeConfigurationByBaseArticleVO, CompositeConfigurationByGivingAreasVO } from '@article/model/vo/composite-article-configuration.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { SaveAsCompositeMode, SavedProjectData, SaveProjectService } from '@project/components/project/project-save/save-project.service';
import { ProjectModel } from '@project/model/project.model';
import { DateFormatter } from '@shared/date-formatter';
import { StringUtils } from '@shared/util/string-utils';
import { UserModel } from '@user/model/user.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectNavigationService } from '../service/project-navigation.service';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { MarkingSystemProjectCreationService } from '@configuration/wizard/service/marking-system-project-creation.service';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { CustomValidators } from '@shared/custom-validators';
import { lastValueFrom, Subscription } from 'rxjs';
import { UserSettings } from '@user/model/user-settings';
import { ProjectDataService } from '@project/service/project-data.service';
import { UserUtils } from '@user/service/user-utils';
import { IsProjectType } from '@project/model/is-project-type';
import { UserComponentPresenter } from '@user/components/user-component.presenter';

@Component({
  standalone: false,
  templateUrl: './save-project.component.html',
  selector: 'app-save-project'
})

export class SaveProjectComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() public finish = new EventEmitter<ProjectNavigationResult<ProjectVO>>();

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;

  private projectNavigationComponent: Signal<ProjectNavigationComponent> = viewChild(ProjectNavigationComponent);

  public model: ProjectNavigationModel;

  public submitEnabled = false;
  public projectSavedHandlerSubscription: Subscription;
  public visibleCompositeCheckBox = true;

  public headerTitle: string;
  public headerText: string;

  public constructor(
    private projectModel: ProjectModel,
    private userModel: UserModel,
    private userSettings: UserSettings,
    private alertHelper: AlertHelper,
    private saveProjectService: SaveProjectService,
    private projectNavigationService: ProjectNavigationService,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private markingSystemProjectCreationService: MarkingSystemProjectCreationService,
    private userComponentPresenter: UserComponentPresenter
  ) {}

  public ngOnDestroy() {
    this.unsubscribeSaveHandlers();
  }

  public async ngOnInit() {
    this.model = new ProjectNavigationModel();
    this.model.currentModel = {
      importType: null,
      lastSelectedGroupId: this.userSettings.lastSelectedProjectGroupId,
      lastSelectedProjectId: this.userSettings.lastSelectedProjectId,
      preselectGroup: false,
      visibleSharedProjects: true,
      visibleSaveBlock: true,
      visibleHiddenProjects: true
    };

    this.visibleCompositeCheckBox = this.projectNavigationService.isSaveLoadCompositeArticlesAvailable;

    if (this.userModel.isGuestUser()) {
      await this.userComponentPresenter.presentReloginComponent(true, false, true);
      if (this.userModel.isGuestUser()) {
        // As we do not get any return value from the Relogin dialog itself, we test, whether the user is still a guest user.
        // If yes, we close the save dialog
        this.cancel();
        return;
      }
    }
    await this.projectNavigationService.openPreselectedProjectGroup(this.model);
    this.updateSubmitEnabled();
    this.addProjectSaveHandlers();
    this.model.xcomSaveAvailable = this.xcomSaveAvailable();
    this.model.addTimestampToProjectNameAvailable = this.isAddTimestampAvailable();
    this.headerTitle = 'projectNavigation.title_save_configuration';
    this.headerText = 'projectNavigation.text_save_configuration';
  }

  private xcomSaveAvailable(): boolean {
    return (
      !this.model.compositeCheckBoxValue &&
      UserUtils.hasSaveProjectWithSubprojectsPermission(this.userModel.user) &&
        this.projectService.isXcomSubprojectAvailable(this.projectModel.currentProject)
    );
  }

  private isAddTimestampAvailable(): boolean {
    return (
      this.userModel.user.hasPermission("ADD_TIMESTAMP_TO_PROJECT_NAME") &&
      (IsProjectType.markingSystem(this.projectModel.currentProject) || IsProjectType.mcsGds(this.projectModel.currentProject))
    );
  }

  public ngAfterViewInit() {
    this.projectNavigationComponent()?.initNewConfigNameIfNecessary();
  }

  private addProjectSaveHandlers() {
    if (!this.projectSavedHandlerSubscription) {
      this.projectSavedHandlerSubscription = this.saveProjectService.savedProject$.subscribe((projectSavedData) => this.savedProject(projectSavedData))
    }
  }

  public unsubscribeSaveHandlers() {
    if (this.projectSavedHandlerSubscription) {
      this.projectSavedHandlerSubscription.unsubscribe();
    }
  }

  private async savedProject(data: SavedProjectData): Promise<void> {
    if (data.success) {
      if (this.userModel.user && this.userModel.user.hasPermission("LOAD_PROJECTS") && !this.model.compositeCheckBoxValue) {
        lastValueFrom(this.projectDataService
          .getLatestProjectVersionNumber(this.projectModel.currentProject.id))
          .then((currentVersion) => {
            this.projectModel.latestProjectVersionNumber = currentVersion;
          });
      }
      await this.markingSystemProjectCreationService.updateMarkingSystemPredefinedAreas();

      this.finish.emit({success: true, result: data.projectVO});
    } else {
      this.finish.emit({success: false});
    }
  }

  public updateSubmitEnabled(): void {
    this.submitEnabled = this.isActionButtonEnabledSaveMode();
    this.validateProjectName();
  }

  private validateProjectName(): void {
    const isValid: boolean = CustomValidators.checkProjectNameIsValid(
      this.model.newConfigName,
    );
    if (!isValid) {
      this.submitEnabled = false;
      this.model.nameError = true;
      return;
    }
    this.model.nameError = false;
  }

  private isActionButtonEnabledSaveMode(): boolean {
    if (this.projectModel.isNewConfigurationGroup &&
      (this.model.currentTab === ProjectNavigationTab.Pinned || this.model.currentTab === ProjectNavigationTab.RecentlyUsed)
    ) {
      return false;
    }

    if (!this.model.currentSelectedItem && !this.model.currentViewedProjectGroup) {
      return false;
    }

    else if (
      this.model.currentSelectedItem instanceof ProjectGroupVO &&
      !ProjectGroupUtils.isConfigurationGroupType(this.model.currentSelectedItem.groupType)
    ) {
      return false;
    } else if (
      (!this.model.compositeCheckBoxValue || this.model.saveMode === this.model.SAVE_AS_BUILDING_BLOCK) &&
      StringUtils.isNotEmpty(this.model.newConfigName)
    ) {
      return true;
    } else if (
      this.model.compositeCheckBoxValue &&
      this.model.saveMode !== this.model.SAVE_AS_BUILDING_BLOCK
    ) {
      return this.model.saveFormIsValid;
    } else {
      return false;
    }
  }

  public async saveSelectedProject(createSubprojects: boolean = false): Promise<void> {
    await this.trySaveAsNewProject(createSubprojects);
    this.projectNavigationComponent()?.updateSubmitEnabled();
  }

  private getSaveDestination(): ProjectGroupVO | null {
    if(this.model.currentViewedProjectGroup) {
      return this.model.currentViewedProjectGroup;
    }

    if (this.model.currentSelectedItem instanceof ProjectGroupVO) {
      return this.model.currentSelectedItem;
    }

    if (this.model.currentSelectedItem instanceof ProjectHeadVO) {
      return this.model.currentProjectFolder.groups.find(
        (g) => g.id === (this.model.currentSelectedItem as ProjectVO).projectGroup.id
      );
    }
  }

  private async trySaveAsNewProject(createSubprojects: boolean = false): Promise<void> {
    const configName: string = this.getNewConfigurationName();
    const compositeArticleConfiguration: CompositeArticleConfigurationVO = this.getCompositeArticleConfiguration(configName);
    if (configName) {
      let saveDestination: ProjectGroupVO = this.getSaveDestination();

      if(!saveDestination) {
        await this.alertHelper.showError("global.message.unexpected_error", "");
        return;
      }

      const isPinned: boolean = this.model.currentTab === ProjectNavigationTab.Pinned;
      await this.saveProjectService.save(
        configName,
        saveDestination,
        saveDestination.projects,
        this.getCompositeSaveMode(),
        createSubprojects,
        this.model.baseArticleId,
        compositeArticleConfiguration,
        true,
        isPinned
      );
    }
  }

  private getCompositeArticleConfiguration(configName: string): CompositeArticleConfigurationVO {
    let compositeArticleConfiguration: CompositeArticleConfigurationVO;

    if (this.model.compositeCheckBoxValue) {
      compositeArticleConfiguration =
        this.model.saveMode === this.model.SAVE_AS_BUILDING_BLOCK
          ? null
          : this.createCompositeArticleConfiguration(Number(configName));
    } else {
      compositeArticleConfiguration = null;
    }

    return compositeArticleConfiguration;
  }

  private createCompositeArticleConfiguration(compositeArticleId: number): CompositeArticleConfigurationVO {
    let compositeArticleConfiguration: CompositeArticleConfigurationVO;
    const behaviorProperties: PropertyVO[] = [];
    PropertyUtils.setBooleanPropertyValue(
      behaviorProperties,
      ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_SELECTION_ALLOWED,
      this.model.compositePartSelectionAllowed
    );
    PropertyUtils.setBooleanPropertyValue(
      behaviorProperties,
      ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_MODIFICATION_ALLOWED,
      this.model.compositePartModificationAllowed
    );
    PropertyUtils.setBooleanPropertyValue(
      behaviorProperties,
      ArticleBehaviorPropertyDef.IS_INTERNAL,
      this.model.compositeArticleIsInternal
    );
    PropertyUtils.setBooleanPropertyValue(
      behaviorProperties,
      ArticleBehaviorPropertyDef.IS_INITIAL_ROTATION_IN_MCS_GDS_REQUIRED,
      this.model.compositeArticleInitiallyRotated
    );

    switch (this.model.saveMode) {
      case this.model.SAVE_MODE_SINGLE_BASE_ARTICLE:
        PropertyUtils.setStringPropertyValue(
          behaviorProperties,
          ArticleBehaviorPropertyDef.COMPOSITE_ARTICLE_TYPE,
          CompositeArticleUtils.COMPOSITE_TYPE_SINGLE_BASE
        );
        compositeArticleConfiguration = new CompositeConfigurationByBaseArticleVO();
        compositeArticleConfiguration.compositeArticleId = compositeArticleId;
        compositeArticleConfiguration.behaviorProperties = behaviorProperties;
        (compositeArticleConfiguration as CompositeConfigurationByBaseArticleVO).baseArticleId = Number(
          this.model.baseArticleId
        );
        (compositeArticleConfiguration as CompositeConfigurationByBaseArticleVO).baseArticleVersion = Number(
          this.model.baseArticleVersion
        );
        (compositeArticleConfiguration as CompositeConfigurationByBaseArticleVO).baseArticleUsageId =
          this.model.selectedBaseArticleUsageId;
        break;
      case this.model.SAVE_MODE_MULTIPLE_BASE_ARTICLES:
        PropertyUtils.setStringPropertyValue(
          behaviorProperties,
          ArticleBehaviorPropertyDef.COMPOSITE_ARTICLE_TYPE,
          CompositeArticleUtils.COMPOSITE_TYPE_MULTIPLE_BASE
        );
        PropertyUtils.setBooleanPropertyValue(
          behaviorProperties,
          ArticleBehaviorPropertyDef.IS_COMPOSITE_ARTICLE_STICK_TOGETHER,
          this.model.compositeArticleStickTogether
        );
        compositeArticleConfiguration = new CompositeConfigurationByGivingAreasVO();
        compositeArticleConfiguration.compositeArticleId = compositeArticleId;
        compositeArticleConfiguration.behaviorProperties = behaviorProperties;
        break;
    }

    return compositeArticleConfiguration;
  }


  private getCompositeSaveMode() {
    let compositeMode = SaveAsCompositeMode.NONE;
    if (this.model.compositeCheckBoxValue) {
      compositeMode =
        this.model.saveMode === this.model.SAVE_AS_BUILDING_BLOCK
          ? SaveAsCompositeMode.BUILDING_BLOCK
          : SaveAsCompositeMode.ARTICLE;
    }
    return compositeMode;
  }

  private getCompositeProjectName(): string {
    if (this.model.saveMode === this.model.SAVE_AS_BUILDING_BLOCK) {
      return this.model.newConfigName;
    } else {
      return this.model.compositeArticleId.toString()
        ? this.model.compositeArticleId.toString()
        : this.model.newConfigName;
    }
  }

  private getNewConfigurationName(): string {
    let configName: string = this.model.compositeCheckBoxValue ? this.getCompositeProjectName() : this.model.newConfigName;
    configName = this.replaceUnusedCharacters(configName);
    // SMAR-158: Timestamp now gets appended even in case project name already has a timestamp.
    if (
      !this.projectModel.isNewConfigurationGroup &&
      !this.model.compositeCheckBoxValue &&
      this.model.addTimestampToProjectName &&
      this.model.addTimestampToProjectNameAvailable
    ) {
      configName = configName + '_' + DateFormatter.formatTimestamp(new Date());
    }
    return this.replaceUnusedCharacters(configName);
  }

  private replaceUnusedCharacters(str: string): string {
    if (StringUtils.isEmpty(str)) {
      return str;
    }
    return StringUtils.replaceAll(str.trim(), '\\t', ' ');
  }

  public cancel() {
    this.finish.emit({success: true, result: undefined});
  }
}
